import type { JSX } from "react";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useMe } from "./lib/auth.ts";
import { Layout } from "./components/Layout.tsx";
import { Loading } from "./components/ui.tsx";
import { Login } from "./pages/Login.tsx";
import { ResetPassword } from "./pages/ResetPassword.tsx";
import { Setup } from "./pages/Setup.tsx";
import { Home } from "./pages/Home.tsx";
import { ModuleList, ModuleRecord } from "./pages/ModulePages.tsx";
import { LeadKanban } from "./pages/LeadKanban.tsx";
import { ConvertLead } from "./pages/ConvertLead.tsx";
import { DealList, DealKanban, DealDetail } from "./pages/Deals.tsx";
import { QuoteList, QuoteDetail } from "./pages/Quotes.tsx";
import { Activities } from "./pages/Activities.tsx";
import { Calendar } from "./pages/Calendar.tsx";
import { Reports } from "./pages/Reports.tsx";
import { Settings } from "./pages/Settings.tsx";
import { Users } from "./pages/Users.tsx";
import { PublicForm } from "./pages/PublicForm.tsx";
import { initTheme } from "./lib/theme.ts";
import "./index.css";
import { MarketingLayout } from "./pages/marketing/MarketingLayout.tsx";
import { MktDashboard } from "./pages/marketing/MktDashboard.tsx";
import { MktContacts } from "./pages/marketing/MktContacts.tsx";
import { MktCampaigns } from "./pages/marketing/MktCampaigns.tsx";
import { MktLandings } from "./pages/marketing/MktLandings.tsx";
import { MktFlows } from "./pages/marketing/MktFlows.tsx";
import { MktForms } from "./pages/marketing/MktForms.tsx";
import { MktSurveys } from "./pages/marketing/MktSurveys.tsx";
import { MktFunnels } from "./pages/marketing/MktFunnels.tsx";

initTheme();

const qc = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/** Guarda de sesión: exige /auth/me; envuelve las rutas privadas en el Layout. */
function RequireAuth(): JSX.Element {
  const me = useMe();
  const navigate = useNavigate();
  const client = useQueryClient();

  useEffect(() => {
    const onUnauth = (): void => {
      client.setQueryData(["me"], null);
      navigate("/login", { replace: true });
    };
    window.addEventListener("tiger:unauthenticated", onUnauth);
    return () => window.removeEventListener("tiger:unauthenticated", onUnauth);
  }, [client, navigate]);

  if (me.isLoading) return <div className="min-h-screen grid place-items-center"><Loading /></div>;
  if (!me.data) return <Navigate to="/login" replace />;
  return <Layout />;
}

function NotFound(): JSX.Element {
  return (
    <div className="grid place-items-center py-24 text-center">
      <div>
        <div className="text-5xl mb-3">🔍</div>
        <p className="text-ink-secondary">Página no encontrada.</p>
      </div>
    </div>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("#root no encontrado");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/f/:token" element={<PublicForm />} />

          <Route element={<RequireAuth />}>
            <Route path="/" element={<Home />} />

            <Route path="/leads" element={<ModuleList moduleKey="leads" />} />
            <Route path="/leads/kanban" element={<LeadKanban />} />
            <Route path="/leads/:id" element={<ModuleRecord moduleKey="leads" />} />
            <Route path="/leads/:id/convert" element={<ConvertLead />} />

            <Route path="/contacts" element={<ModuleList moduleKey="contacts" />} />
            <Route path="/contacts/:id" element={<ModuleRecord moduleKey="contacts" />} />

            <Route path="/accounts" element={<ModuleList moduleKey="accounts" />} />
            <Route path="/accounts/:id" element={<ModuleRecord moduleKey="accounts" />} />

            <Route path="/products" element={<ModuleList moduleKey="products" />} />
            <Route path="/products/:id" element={<ModuleRecord moduleKey="products" />} />

            <Route path="/terceros" element={<ModuleList moduleKey="terceros" />} />
            <Route path="/terceros/:id" element={<ModuleRecord moduleKey="terceros" />} />

            <Route path="/deals" element={<DealList />} />
            <Route path="/deals/kanban" element={<DealKanban />} />
            <Route path="/deals/:id" element={<DealDetail />} />

            <Route path="/quotes" element={<QuoteList />} />
            <Route path="/quotes/:id" element={<QuoteDetail />} />

            <Route path="/activities" element={<Activities />} />
            <Route path="/activities/calendar" element={<Calendar />} />

            <Route path="/marketing" element={<MarketingLayout />}>
              <Route index element={<MktDashboard />} />
              <Route path="contactos/*" element={<MktContacts />} />
              <Route path="campanas/*" element={<MktCampaigns />} />
              <Route path="landings/*" element={<MktLandings />} />
              <Route path="automatizaciones/*" element={<MktFlows />} />
              <Route path="formularios/*" element={<MktForms />} />
              <Route path="encuestas/*" element={<MktSurveys />} />
              <Route path="embudos/*" element={<MktFunnels />} />
            </Route>

            <Route path="/reports" element={<Reports />} />
            <Route path="/users" element={<Users />} />
            <Route path="/settings" element={<Settings />} />

            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
